/**
 * SwipeHintTooltip Component
 *
 * Reusable animated tooltip for swipe/scroll hints.
 * Used for mobile gesture hints like "swipe to minimize" or "swipe to see more".
 *
 * Features:
 * - Bounce entrance animation
 * - Continuous pulse animation
 * - Fade-out animation on dismissal
 * - Supports absolute and fixed positioning
 * - 100% CSS Modules
 *
 * @module components/ui/SwipeHintTooltip
 */

import { memo } from 'react';
import type { ReactNode } from 'react';
import classes from './SwipeHintTooltip.module.css';

export interface SwipeHintTooltipProps {
  /** Content to display (usually translated text) */
  children: ReactNode;
  /** Whether the tooltip is visible */
  visible: boolean;
  /** Whether the tooltip is fading out (before hide) */
  fadingOut?: boolean;
  /** Positioning mode - fixed for viewport-relative, absolute for parent-relative */
  position?: 'absolute' | 'fixed';
  /** Additional CSS class for custom positioning */
  className?: string;
  /** Data attributes for CSS targeting (e.g., data-above-minimized-form) */
  dataAttributes?: Record<string, string | boolean | undefined>;
  /** ARIA role for accessibility */
  role?: 'status' | 'alert';
}

/**
 * SwipeHintTooltip Component
 *
 * Animated hint tooltip with consistent styling across the app
 */
export const SwipeHintTooltip = memo(function SwipeHintTooltip({
  children,
  visible,
  fadingOut = false,
  position = 'fixed',
  className = '',
  dataAttributes = {},
  role = 'status',
}: SwipeHintTooltipProps) {
  if (!visible) {
    return null;
  }

  // Build data attributes object
  const dataProps: Record<string, string | undefined> = {
    'data-position': position,
  };

  // Add custom data attributes (filter out undefined/false values)
  Object.entries(dataAttributes).forEach(([key, value]) => {
    if (value !== undefined && value !== false) {
      dataProps[key] = String(value);
    }
  });

  return (
    <div
      className={`${classes.hint} ${fadingOut ? classes.fadingOut : ''} ${className}`}
      role={role}
      aria-live="polite"
      {...dataProps}
    >
      {children}
    </div>
  );
});
